import React from 'react'

import PropTypes from 'prop-types'
import { pick } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import EditIcon from '@mui/icons-material/Edit'
import { IconButton, List, ListItem } from '@mui/material'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import * as Yup from 'yup'

import { useAllowed } from '~/src/Flags'
import createLogger from '~/src/Lib/Logging'
import { getDateDiff } from '~/src/Lib/Utils'
import ArrowDown from '~/src/UI/Icons/ArrowDown'
import ButtonSelect from '~/src/UI/Shared/ButtonSelect'
import EntityPrimaryColor from '~/src/UI/Shared/EntityPrimaryColor'
import PageTitle from '~/src/UI/Shared/PageTitle'
import AutosaveTextField from '~/src/UI/Shared/AutosaveTextField'
import T from '~/src/UI/Shared/Typography'
import { buildThemeFromColor } from '~/src/UI/Theme/mui'

const displayName = 'Harvest/ActiveChooser'
const logger = createLogger(displayName)

const useStyles = makeStyles(theme => ({
  activeHarvestChooserSubheader: {
    margin: '15px 16px',
  },
  activeHarvestChooserList: {
    minWidth: 320,
  },
  activeHarvestChooserListItem: {
    fontSize: 14,
    display: 'flex',
    justifyContent: 'space-between',
  },
  harvestNameTitleField: {
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      '& >:first-child > [class*=MuiButton]': {
        backgroundColor: theme.palette.batch.enabled,
      },
      '& > button:last-child': {
        opacity: 1,
      },
    },
    '& > button:last-child': {
      marginRight: 4,
      opacity: 0,
      transition: [
        `background-color ${theme.transitions.duration.short}ms`,
        `opacity ${theme.transitions.duration.short}ms`,
      ].join(', '),
    },
  },
  headingInput: {
    ...theme.typography.h1,
  },
}), { name: displayName })

const ActiveHarvestChooser = ({ currentHarvest }) => {
  const {
    harvests,
    heights,
    phases,
    routeInfo,
    doHarvestUpdate,
    doUpdateUrl,
  } = useConnect(
    'selectHarvests',
    'selectHeights',
    'selectPhases',
    'selectRouteInfo',
    'doHarvestUpdate',
    'doUpdateUrl',
  )
  const classes = useStyles()

  const [canChangeHarvest] = useAllowed({ actions: 'change_harvest' })

  const activeHarvests = Object.values(harvests)
    .filter(harv => getDateDiff(harv.startDate).daysDiff >= 0 && getDateDiff(harv.endDate).daysDiff <= 0)

  const titleText = (
    <T variant={heights.window < 768 ? 'h2' : 'h1'} noWrap>{currentHarvest.name}</T>
  )

  const harvestSelector = (
    <ButtonSelect
      btnTitle={titleText}
      data-testid="active-harvest-chooser"
      variant="text"
      sx={{
        height: '3.5rem',
        borderRadius: theme => theme.shape.borderRadius,
        py: 0.5,
        px: 1.5,
      }}
      endIcon={<ArrowDown color="action" className="aroya__harvest-chooser__icon" />}
    >
      <List
        className={classes.activeHarvestChooserList}
        subheader={(
          <T className={classes.activeHarvestChooserSubheader}>Currently growing Harvest Groups:</T>
        )}
      >
        {activeHarvests.map(activeHarvest => {
          const { [activeHarvest.currentPhase]: currentPhase } = phases
          return (
            <ListItem
              key={activeHarvest.id}
              className={classes.activeHarvestChooserListItem}
              button
              selected={currentHarvest.id === activeHarvest.id}
              onClick={() => {
                const harvestDetailPattern = /\/harvests\/:id\/(\w+)/
                const found = routeInfo.pattern.match(harvestDetailPattern)
                if (found) {
                  doUpdateUrl(`/harvests/${activeHarvest.id}/${found[1]}`)
                } else {
                  doUpdateUrl(`/harvests/${activeHarvest.id}`)
                }
              }}
            >
              <T color="primary">{activeHarvest.name}</T>
              <EntityPrimaryColor entity={currentPhase} colorKey={currentPhase?.phaseType ? undefined : 'PROP'}>
                {({ primary }) => (
                  <StyledEngineProvider injectFirst>
                    <ThemeProvider
                      theme={theme => buildThemeFromColor(primary, {
                        adjustColor: true,
                        bg: theme.palette.background.secondary,
                      })}
                    >
                      <T color="primary">
                        {phases[activeHarvest.currentPhase]?.name}
                      </T>
                    </ThemeProvider>
                  </StyledEngineProvider>
                )}
              </EntityPrimaryColor>
            </ListItem>
          )
        })}
      </List>
    </ButtonSelect>
  )

  const title = activeHarvests.length > 1 ? harvestSelector : titleText

  const updateHarvestName = async updatedName => {
    if (!updatedName || updatedName === currentHarvest.name) return
    const updatedHarvest = {
      ...pick(['id', 'cultivars', 'startDate'], currentHarvest),
      name: updatedName,
    }
    await doHarvestUpdate(updatedHarvest)
  }

  const harvestNameField = (
    <AutosaveTextField
      fullWidth
      initialValue={currentHarvest.name}
      InputProps={{ className: classes.headingInput }}
      maxLength={50}
      name="harvestName"
      renderDisplay={({ enterEditMode }) => (
        <div className={classes.harvestNameTitleField}>
          {title}
          <IconButton color="primary" onClick={enterEditMode} size="large">
            <EditIcon />
          </IconButton>
        </div>
      )}
      validationSchema={Yup.string().trim().max(50)}
      onSave={updateHarvestName}
    />
  )

  return (
    <PageTitle
      backButtonColor="secondary"
      component="h1"
      disableTypography
      title={canChangeHarvest ? harvestNameField : title}
      titleText={currentHarvest.name}
      onClickBack={() => doUpdateUrl('/harvests')}
    />
  )
}

ActiveHarvestChooser.propTypes = {
  currentHarvest: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }).isRequired,
}

ActiveHarvestChooser.displayName = displayName

const Memoized = React.memo(ActiveHarvestChooser)

export default Memoized
